'use client';

import { Link } from 'react-router-dom';
import { JsonLd } from '@/seo/JsonLd';
import type { Product } from '@/data/products';
import { products } from '@/data/products';
import { ProductCard } from '@/components/ProductCard/ProductCard';
import { Accordion } from '@/components/Accordion/Accordion';
import { Tabs } from '@/components/Tabs/Tabs';
import { IconWhatsApp, IconPhone, IconStar } from '@/components/Icons/Icons';
import { buildWhatsAppUrl, SALES_PHONE_READABLE } from '@/utils/whatsapp';
import { SITE_DOMAIN, toAbsoluteUrl } from '@/utils/url';
import styles from './ProductDetail.module.css';

const kayseriProduct: Product = {
  slug: 'kayseri-bondex-sunger',
  name: 'Kayseri Bondex Sünger',
  category: 'Bondex Sünger',
  shortDescription:
    'Kayseri bondex sünger; konut, ticari ve üretim odaklı projelerde ses ve titreşim kontrolü için yüksek yoğunluklu çözüm sunar.',
  description:
    'Kayseri bondex sünger; rebonded süngerpan yapısıyla darbe ve titreşim kaynaklı sesleri azaltmaya yardımcı olan, Kayseri içi projelere özel ses yalıtım çözümüdür.',
  imagePath: '/images/bondex-4.webp',
  sku: 'BDX-KYS',
  mpn: 'BDX-KYS',
  priceTRY: 130,
  priceUnit: 'm2',
  faqs: [
    {
      question: 'Kayseri bondex sünger nerelerde kullanılır?',
      answer:
        'Konut, ofis, atölye, depo, teknik oda ve makine hacimlerinde kullanılır. Kayseri’de hem merkez yapı stoğunda hem sanayi hatlarında tercih edilir.',
    },
    {
      question: 'Kayseri içi teslimat süresi nedir?',
      answer:
        'Stok durumuna göre Kayseri merkez ve ilçelerde teslimatlar çoğunlukla 1-3 iş günü içinde planlanır. Büyük metrajlı işlerde etaplı sevkiyat yapılabilir.',
    },
    {
      question: 'Hangi kalınlıklar öneriliyor?',
      answer:
        'Hafif ihtiyaçlarda 10-20mm, orta-yüksek şikayetlerde 30-40mm, zorlu teknik senaryolarda 50mm ve bariyerli kombinasyonlar önerilir.',
    },
    {
      question: "Kayseri'de teknik keşif var mı?",
      answer:
        'Evet. Proje metrajı, kullanım tipi ve hedef konfora göre katman önerisi ve uygulama planlaması yapılır.',
    },
    {
      question: 'Kayseri bondex sünger fiyatı ne kadar?',
      answer:
        "Kayseri bondex sünger fiyatı metrekare (m²) başına 130 TL'den başlar. Net fiyat kalınlık, metraj ve katman kurgusuna göre değişir.",
    },
  ],
  reviews: [
    {
      authorName: 'Cem Yıldız',
      datePublished: '2026-04-04',
      ratingValue: 5,
      title: 'Talas konut yenilemesinde iyi sonuç',
      body:
        "Daire zemininde uyguladık. Üst kat darbe sesi belirgin şekilde azaldı.",
      verified: true,
    },
    {
      authorName: 'Sibel Kaya',
      datePublished: '2026-04-10',
      ratingValue: 5,
      title: 'Melikgazi ofisinde memnun kaldık',
      body:
        "Toplantı odasında ses taşmasını azaltmak için kullandık. Çalışma konforu arttı.",
      verified: true,
    },
    {
      authorName: 'Mert Demir',
      datePublished: '2026-04-16',
      ratingValue: 4,
      title: 'OSB teknik odada fark edildi',
      body:
        'Makine kaynaklı titreşim etkisini düşürmek için uyguladık. Sonuç beklentimizi karşıladı.',
      verified: true,
    },
  ],
};

export function CityBondexKayseri() {
  const url = toAbsoluteUrl('/kayseri-bondex-sunger');
  const title = 'Kayseri Bondex Sünger | Yalıtım Süngeri– Echopan';
  const description =
    'Kayseri bondex sünger, konut ve teknik projelerde darbe ve titreşim kaynaklı sesleri azaltmaya yardımcı olur. Echopan kalitesiyle sunulur.';

  const relatedSlugs = ['bondex-sunger', 'bondex-sunger-20mm', 'bondex-sunger-40mm', 'bariyerli-bondex-sunger'];
  const relatedBondex = relatedSlugs.map((slug) => products.find((p) => p.slug === slug)).filter((p): p is Product => Boolean(p));

  const waUrl = buildWhatsAppUrl('Merhaba, Kayseri bondex sünger için fiyat ve teslimat bilgisi rica ediyorum. (kayseri-bondex-sunger)');
  const displayPrice = kayseriProduct.priceTRY
    ? new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', maximumFractionDigits: 0 }).format(kayseriProduct.priceTRY)
    : null;

  const pageReviews =
    kayseriProduct.reviews?.map((r) => ({
      name: r.authorName,
      date: r.datePublished,
      title: r.title,
      verified: !!r.verified,
      rating: r.ratingValue,
      text: r.body,
    })) || [];
  const avgRating =
    pageReviews.length > 0 ? pageReviews.reduce((sum, r) => sum + (Number.isFinite(r.rating) ? r.rating : 0), 0) / pageReviews.length : 0;

  const topSpecs = [
    { k: 'Stok', v: 'Kayseri içi düzenli stok' },
    { k: 'Teslim', v: '1-3 iş günü' },
    { k: 'Kalınlık', v: '10mm-50mm' },
    { k: 'Uygulama', v: 'Yapıştırma / Mekanik tespit' },
    { k: 'Hizmet', v: 'Kayseri merkez + ilçeler' },
    { k: 'Plan', v: 'Proje bazlı teknik yönlendirme' },
  ];

  const breadcrumb = [
    { name: 'Anasayfa', item: toAbsoluteUrl('/') },
    { name: 'Ürünler', item: toAbsoluteUrl('/urunler') },
    { name: 'Kayseri Bondex Sünger', item: url },
  ];

  const validFromStr = new Date().toISOString().split('T')[0];
  const priceValidUntil = new Date();
  priceValidUntil.setFullYear(priceValidUntil.getFullYear() + 1);
  const priceValidUntilStr = priceValidUntil.toISOString().split('T')[0];

  const schema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebSite',
        '@id': `${toAbsoluteUrl('/')}#website`,
        name: 'Bondex Sünger',
        url: toAbsoluteUrl('/'),
        inLanguage: 'tr-TR',
        potentialAction: {
          '@type': 'SearchAction',
          target: { '@type': 'EntryPoint', urlTemplate: `${toAbsoluteUrl('/')}urunler?q={search_term_string}` },
          'query-input': 'required name=search_term_string',
        },
      },
      {
        '@type': 'Organization',
        '@id': `${toAbsoluteUrl('/')}#organization`,
        name: 'bariyerlibondexsunger.com',
        url: toAbsoluteUrl('/'),
        logo: { '@type': 'ImageObject', url: toAbsoluteUrl('/images/bondex-logo.webp'), width: 512, height: 512 },
        contactPoint: [
          { '@type': 'ContactPoint', telephone: '+90 216 519 34 11', contactType: 'customer service', areaServed: 'TR', availableLanguage: ['tr'] },
          { '@type': 'ContactPoint', telephone: '+90 546 919 11 34', contactType: 'sales', areaServed: 'TR', availableLanguage: ['tr'] },
        ],
        sameAs: [
          'https://www.facebook.com/profile.php?id=61575206163572',
          'https://x.com/KabinPods',
          'https://www.instagram.com/kabinpods/',
          'https://www.youtube.com/channel/UCKk3RjOabrJb8JQYK9BjWkw',
          'https://www.linkedin.com/company/tasar%C4%B1m-akustik-mimarl%C4%B1k',
        ],
      },
      {
        '@type': 'WebPage',
        '@id': `${url}#webpage`,
        url,
        name: title,
        description,
        isPartOf: { '@id': `${toAbsoluteUrl('/')}#website` },
        breadcrumb: { '@id': `${url}#breadcrumb` },
        inLanguage: 'tr-TR',
        datePublished: '2026-04-27',
        dateModified: new Date().toISOString().split('T')[0],
        primaryImageOfPage: { '@type': 'ImageObject', url: toAbsoluteUrl(kayseriProduct.imagePath), width: 1200, height: 750 },
        about: [
          { '@type': 'Thing', name: 'Kayseri Bondex Sünger' },
          { '@type': 'Thing', name: 'Kayseri ses yalıtımı' },
          { '@type': 'Thing', name: 'teknik hacim izolasyonu' },
        ],
        speakable: { '@type': 'SpeakableSpecification', cssSelector: ['.ProductSEO', '.ProductPage h1'] },
        mainEntity: { '@id': `${url}#product` },
      },
      {
        '@type': 'BreadcrumbList',
        '@id': `${url}#breadcrumb`,
        itemListElement: breadcrumb.map((b, idx) => ({ '@type': 'ListItem', position: idx + 1, name: b.name, item: b.item })),
      },
      {
        '@type': 'Product',
        '@id': `${url}#product`,
        name: kayseriProduct.name,
        image: [{ '@type': 'ImageObject', url: toAbsoluteUrl(kayseriProduct.imagePath), width: 1200, height: 750 }],
        description: kayseriProduct.description,
        brand: { '@type': 'Brand', name: 'Bondex' },
        category: kayseriProduct.category,
        url,
        sku: kayseriProduct.sku,
        mpn: kayseriProduct.mpn,
        productID: kayseriProduct.sku || kayseriProduct.mpn,
        material: 'Rebonded Süngerpan',
        audienceType: 'Business',
        additionalProperty: [
          { '@type': 'PropertyValue', name: 'Kategori', value: kayseriProduct.category },
          { '@type': 'PropertyValue', name: 'Tip', value: 'Rebonded Süngerpan' },
          { '@type': 'PropertyValue', name: 'Hizmet Bölgesi', value: 'Kayseri' },
        ],
        offers: {
          '@type': 'Offer',
          url,
          priceCurrency: 'TRY',
          price: String(kayseriProduct.priceTRY),
          validFrom: validFromStr,
          priceValidUntil: priceValidUntilStr,
          availability: 'https://schema.org/InStock',
          itemCondition: 'https://schema.org/NewCondition',
          seller: { '@id': `${toAbsoluteUrl('/')}#organization` },
          unitText: 'm²',
          priceSpecification: { '@type': 'UnitPriceSpecification', price: String(kayseriProduct.priceTRY), priceCurrency: 'TRY', unitCode: 'MTK', unitText: 'm²' },
          hasMerchantReturnPolicy: { '@id': `${url}#merchantReturnPolicy` },
          shippingDetails: { '@id': `${url}#shippingDetails` },
        },
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: avgRating.toFixed(1),
          reviewCount: pageReviews.length,
          bestRating: '5',
          worstRating: '1',
        },
        review: kayseriProduct.reviews?.map((r) => ({
          '@type': 'Review',
          author: { '@type': 'Person', name: r.authorName },
          datePublished: r.datePublished,
          reviewBody: r.body,
          headline: r.title,
          ...(r.verified && { reviewAspect: 'Verification' }),
          reviewRating: { '@type': 'Rating', ratingValue: String(r.ratingValue), bestRating: '5', worstRating: '1' },
        })),
      },
      {
        '@type': 'MerchantReturnPolicy',
        '@id': `${url}#merchantReturnPolicy`,
        applicableCountry: { '@type': 'Country', name: 'TR' },
        returnPolicyCategory: 'https://schema.org/MerchantReturnFiniteReturnWindow',
        merchantReturnDays: 14,
        returnMethod: 'https://schema.org/ReturnByMail',
        returnFees: 'https://schema.org/FreeReturn',
      },
      {
        '@type': 'OfferShippingDetails',
        '@id': `${url}#shippingDetails`,
        shippingRate: { '@type': 'MonetaryAmount', value: '0', currency: 'TRY' },
        shippingDestination: { '@type': 'DefinedRegion', addressCountry: { '@type': 'Country', name: 'TR' } },
        deliveryTime: {
          '@type': 'ShippingDeliveryTime',
          handlingTime: { '@type': 'QuantitativeValue', minValue: 1, maxValue: 3, unitCode: 'DAY' },
          transitTime: { '@type': 'QuantitativeValue', minValue: 1, maxValue: 5, unitCode: 'DAY' },
          businessDays: { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'] },
          cutoffTime: '14:00',
        },
      },
      {
        '@type': 'FAQPage',
        '@id': `${url}#faq`,
        mainEntity: kayseriProduct.faqs?.map((f) => ({ '@type': 'Question', name: f.question, acceptedAnswer: { '@type': 'Answer', text: f.answer } })),
      },
    ],
  };

  const tabItems = [
    {
      key: 'aciklama',
      label: 'Açıklama',
      content: (
        <div className={styles.seoWrap}>
          <section className={styles.seoCard}>
            <h2 className={styles.seoTitle}>Kayseri'de Bondex Sünger Kullanımında Doğru Yaklaşım</h2>
            <p className={styles.seoText}>
              Kayseri'de ses yalıtımı ihtiyacı tek bir cümleyle anlatılamayacak kadar farklı senaryolardan oluşur. Şehirde bir yanda Talas,
              Melikgazi ve Kocasinan hattında yoğun konut ve ofis kullanımı, diğer yanda Organize Sanayi bölgesi ve üretim odaklı alanlarda
              teknik hacim kaynaklı titreşim problemleri bulunur. Bu farklılık nedeniyle "her projeye aynı kalınlık" yaklaşımı hem maliyet
              hem performans açısından hatalı sonuç verir. Sağlıklı sonuç için önce sesin kaynağı, sonra iletim yolu, en son ürün kalınlığı
              belirlenmelidir.
            </p>
            <p className={styles.seoText}>
              Kayseri bondex sünger, rebonded süngerpan yapısı sayesinde darbe ve titreşim kaynaklı enerjinin yüzeyler arasında taşınmasını
              azaltmaya yardımcı olan bir katman çözümüdür. Ancak malzemenin tek başına iyi olması yeterli değildir. Uygulama başarısı,
              yüzey hazırlığı, birleşim detayları, kenar sürekliliği, tesisat geçişlerinin kontrolü ve uygulama sırası gibi faktörlerle
              birlikte değerlendirilmelidir. Sahada en sık yapılan hata, yalnızca ürün seçimine odaklanıp uygulama disiplinini ikinci plana
              atmaktır.
            </p>
            <h3 className={styles.seoSubtitle}>Konut ve Ofis Senaryoları</h3>
            <p className={styles.seoText}>
              Konut projelerinde en sık şikayetler üst kat adım sesi, sürükleme sesi ve komşu duvarlardan gelen konuşma geçişidir. Ofis
              projelerinde ise toplantı odası mahremiyeti, açık ofis uğultusu ve teknik alanlardan yayılan sürekli ses öne çıkar. Bu iki
              grup benzer görünse de teknik olarak farklı şekilde ele alınmalıdır. Konutta rahatsızlığı azaltan hedefler çoğunlukla zemin
              ve duvar geçişlerini kapsarken, ofis tarafında sesin taşındığı zayıf noktaları tespit etmek daha önemlidir.
            </p>
            <p className={styles.seoText}>
              Kayseri'de apartman dönüşüm projelerinde kullanıcıların en büyük beklentisi "gece sessizliği" olur. Bu beklentiye karşılık
              verilirken, tam sessizlik yerine ölçülebilir ve sürdürülebilir konfor hedeflenmelidir. Böylece hem gerçekçi bir teknik
              yaklaşım kurulmuş olur hem de bütçe tarafında gereksiz kalınlık kullanımının önüne geçilir. Özellikle daire içinde yaşamın
              devam ettiği projelerde etaplı çalışma modeli, hem uygulama hızını hem kullanıcı memnuniyetini artırır.
            </p>
            <h3 className={styles.seoSubtitle}>Teknik Hacim ve Üretim Alanı</h3>
            <p className={styles.seoText}>
              Teknik hacimlerde kaynak ses ve titreşim birlikte ele alınmalıdır. Kompresör, fan ve makine odası gibi alanlarda kaynak
              noktaya yakın çözümleme yapılmazsa performans düşebilir. Gerekli durumlarda{' '}
              <Link to="/bariyerli-bondex-sunger" rel="follow">bariyerli bondex</Link> sistemleriyle hibrit planlama yapılabilir.
            </p>
            <p className={styles.seoText}>
              Üretim hatlarında karşılaşılan temel sorunlardan biri, teknik odadaki enerjinin taşıyıcı sistem üzerinden komşu alanlara
              aktarılmasıdır. Bu durumda yalnızca duvar yüzeyi kaplamak yeterli olmaz; geçiş noktaları, ekipman temasları ve bağlantı
              detayları birlikte ele alınmalıdır. Kayseri OSB tarafında bu tip projelerde bakım erişiminin korunması da önemlidir.
              Uygulama sonrası servis alanları kapanırsa işletme tarafında yeni maliyetler oluşabilir.
            </p>
            <h3 className={styles.seoSubtitle}>Kalınlık Seçimi</h3>
            <p className={styles.seoText}>
              20-30mm aralığı çoğu konut/ofis işinde dengeli sonuç verirken, yüksek şikayetli alanlarda 40-50mm çözümler gündeme gelebilir.
              Doğru kalınlık kararı, şikayet tipi ve uygulama yüzeyi birlikte değerlendirilerek verilmelidir.
            </p>
            <p className={styles.seoText}>
              Kalınlık kararında yapılan en büyük hata, yalnızca "daha kalın daha iyi" mantığıdır. Teknik olarak doğru yaklaşım,
              problemlerin yoğunlaştığı bölgeleri güçlendirip düşük riskli bölgelerde optimize seçim yapmaktır. Bu yöntem, toplam
              proje maliyetini kontrol ederken performansın kullanıcı tarafından daha net hissedilmesini sağlar. Ayrıca proje süresi
              açısından da daha verimli bir planlama sunar.
            </p>
            <h3 className={styles.seoSubtitle}>Kayseri'de Proje Planlamasında Kritik Adımlar</h3>
            <p className={styles.seoText}>
              Sağlıklı bir proje akışı üç temel adımda yürütülmelidir: ihtiyaç analizi, uygulama disiplini, sonuç doğrulaması. İhtiyaç
              analizinde sesin türü ve kaynağı belirlenir. Uygulama disiplininde yüzey hazırlığı, katman yerleşimi ve birleşim kontrolü
              yapılır. Son aşamada ise hedeflenen konfor seviyesi ile elde edilen sonuç karşılaştırılır. Bu yapı kurulmadan yapılan işler,
              kısa vadede kabul edilebilir görünse de uzun vadede tekrar maliyeti doğurabilir.
            </p>
            <p className={styles.seoText}>
              Konya, Ankara veya İstanbul gibi örnek şehirlerde olduğu gibi Kayseri'de de şehir içi yapı stoğu homojen değildir. Bir mahallede
              yeni yapı stoğu varken birkaç kilometre ötede eski yapı detayları görülebilir. Bu yüzden her işte aynı uygulama reçetesi
              kullanmak yerine, proje bazlı teknik değerlendirme yapmak gerekir. Bu yaklaşım, şehir bazlı landing sayfalarının da gerçek değerini
              artırır çünkü kullanıcıya gerçekten kendi senaryosuna yakın içerik sunulmuş olur.
            </p>
            <h3 className={styles.seoSubtitle}>Yüzey Hazırlığı ve Birleşim Kontrolü Neden Bu Kadar Önemli?</h3>
            <p className={styles.seoText}>
              Uygulama kalitesini belirleyen ana faktörlerden biri yüzey hazırlığıdır. Tozlu, nemli veya bozuk yüzeye yapılan uygulamalarda
              yapışma kalitesi düşer ve katman sürekliliği bozulur. Bununla birlikte köşe dönüşleri, ek yerleri, tesisat geçişleri ve kapı
              çevresi gibi detaylar açık bırakılırsa ses en zayıf noktadan kaçmaya devam eder. Bu nedenle "malzeme kaliteli, iş bitti"
              yaklaşımı teknik olarak geçerli değildir.
            </p>
            <p className={styles.seoText}>
              Kayseri projelerinde ara kalite kontrol adımı uygulandığında tekrar iş oranı belirgin şekilde düşer. Katman yerleşimi tamamlandıktan
              sonra hızlı bir kontrol turu ile sorunlu bölgeler erken tespit edilir ve aynı etapta düzeltilir. Bu yöntem, hem zaman kaybını
              azaltır hem de teslim sonrası memnuniyeti artırır.
            </p>
            <h3 className={styles.seoSubtitle}>Lojistik ve Termin Yönetimi</h3>
            <p className={styles.seoText}>
              Kayseri içi projelerde teslimat planı yalnızca mesafe hesabıyla yapılmamalıdır. Şantiye erişimi, yük indirme alanı, kat
              taşıma koşulları, çalışma saatleri ve bina yönetimi izinleri birlikte değerlendirilmelidir. Büyük metrajlı işlerde etaplı
              sevkiyat, sahadaki malzeme yoğunluğunu azaltarak uygulama verimini yükseltir.
            </p>
            <p className={styles.seoText}>
              Uygulama ekipleri ile sevkiyat programı aynı takvimde buluşmadığında iş uzar. Bu nedenle proje başlangıcında kısa ama net bir
              termin planı hazırlanmalı ve tüm ekipler bu plana göre ilerlemelidir. Özellikle aktif kullanılan konut veya ofis projelerinde
              saat planı doğru yapılırsa kullanıcı tarafındaki operasyonel rahatsızlık minimuma iner.
            </p>
            <h3 className={styles.seoSubtitle}>Maliyet-Performans Dengesini Korumak</h3>
            <p className={styles.seoText}>
              En yüksek maliyetli sistem her zaman en doğru çözüm değildir. Amaç, hedeflenen konfor düzeyini gereksiz maliyet üretmeden
              sağlamaktır. Kayseri'de konut projelerinde çoğu zaman dengeli sistem yeterli olurken, teknik hacimlerde hedefe göre
              güçlendirilmiş katmanlar gerekebilir. Bu fark doğru analiz edilmezse bütçe verimsiz kullanılır.
            </p>
            <p className={styles.seoText}>
              Proje sahibinin doğru karar vermesi için metraj ve birim fiyat kadar senaryo temelli teknik açıklamaya da ihtiyacı vardır.
              Hangi kalınlığın neden seçildiği, hangi alanda hangi etkiyi hedeflediği net anlatıldığında karar süreci hızlanır ve uygulama
              sonrası memnuniyet artar.
            </p>
            <h3 className={styles.seoSubtitle}>Kayseri'ye Özel İçerik ve Doğru Yönlendirme</h3>
            <p className={styles.seoText}>
              Bu sayfadaki içerik, şehir adı değiştirilmiş kopya metin yaklaşımıyla hazırlanmadı. Kayseri'nin konut, ofis ve teknik hacim
              gerçekleri dikkate alınarak kurgulandı. Amaç yalnızca arama görünürlüğü değil, kullanıcıya kendi senaryosuna uygun teknik
              yönlendirme sunmaktır.
            </p>
            <p className={styles.seoText}>
              Projeniz için net bir öneri almak isterseniz kullanım tipi, metraj, mevcut şikayet tanımı ve hedeflenen konfor seviyesini
              paylaşmanız yeterlidir. Bu verilerle doğru kalınlık ve doğru katman kurgusu daha güvenli şekilde belirlenebilir.
            </p>
          </section>
        </div>
      ),
    },
    {
      key: 'teknik',
      label: 'Teknik Özellikler',
      content: (
        <div className={styles.seoWrap}>
          <section className={styles.seoCard}>
            <h2 className={styles.seoTitle}>Teknik Özellikler Özeti</h2>
            <p className={styles.seoText}>
              Yüksek yoğunluklu yapı, darbe ve titreşim etkisinin yönetiminde destek sağlar. Uygulama başarısı, birleşim kalitesi ve yüzey
              hazırlığı ile birlikte değerlendirilmelidir.
            </p>
          </section>
        </div>
      ),
    },
    {
      key: 'kullanim',
      label: 'Kullanım Alanları',
      content: (
        <div className={styles.seoWrap}>
          <section className={styles.seoCard}>
            <h2 className={styles.seoTitle}>Kayseri'de Kullanım Alanları</h2>
            <p className={styles.seoText}>
              Konut, ofis, teknik oda, atölye ve depo projelerinde kullanılır. Proje senaryosuna göre zemin, duvar veya teknik hacim katmanında çözüm kurulur.
            </p>
          </section>
        </div>
      ),
    },
    {
      key: 'opsiyonel',
      label: 'Opsiyonel',
      content: (
        <div className={styles.seoWrap}>
          <section className={styles.seoCard}>
            <h2 className={styles.seoTitle}>Teslimat ve Uygulama Planı</h2>
            <p className={styles.seoText}>
              Kayseri içi sevkiyat ve uygulama planı şantiye erişimine göre yapılır. Büyük metrajlarda etaplı teslimat ve teknik kontrol adımlarıyla süreç daha verimli yönetilir.
            </p>
          </section>
        </div>
      ),
    },
  ];

  return (
    <main className="container ProductPage">
      <JsonLd data={schema} />

      <nav className={styles.breadcrumb} aria-label="Breadcrumb">
        <Link to="/">Anasayfa</Link>
        <span aria-hidden="true">/</span>
        <Link to="/urunler">Ürünler</Link>
        <span aria-hidden="true">/</span>
        <span aria-current="page">Kayseri Bondex Sünger</span>
      </nav>

      <div className={styles.heroGrid}>
        <div className={styles.media}>
          <img className={styles.img} src={kayseriProduct.imagePath} alt="Kayseri bondex sünger uygulaması" loading="eager" width="600" height="400" />
        </div>
        <section className={`ProductSEO card ${styles.panel}`}>
          <h1 className={styles.h1}>Kayseri Bondex Sünger</h1>
          <p className={styles.modelLine}>
            <span className={styles.modelLabel}>Model:</span> BDX-KYS | Kayseri İçi Projeler
          </p>
          <p className={styles.meta}>
            Kayseri'de <strong>bondex sünger</strong> ihtiyacı olan konut, ofis ve teknik hacim projelerinde doğru kalınlık, hızlı tedarik ve
            proje bazlı teknik planlama sunuyoruz.
          </p>

          {pageReviews.length > 0 && (
            <div className={styles.ratingRow}>
              <div className={styles.stars}>
                {[1, 2, 3, 4, 5].map((star) => (
                  <IconStar key={star} style={{ opacity: star <= Math.round(avgRating) ? 1 : 0.3 }} />
                ))}
              </div>
              <span className={styles.ratingText}>
                {avgRating.toFixed(1)} ({pageReviews.length} değerlendirme)
              </span>
            </div>
          )}

          {displayPrice && (
            <div className={styles.priceRow}>
              <span className={styles.price}>{displayPrice}</span>
              <span className={styles.priceUnit}>/ m²'den başlayan</span>
            </div>
          )}

          <div className={styles.badges}>
            <span className="chip">Bondex Sünger</span>
            <span className="chip">Kayseri içi hızlı teslimat</span>
            <span className="chip">Konut ve teknik alan uyumlu</span>
          </div>

          <div className={styles.specGrid} aria-label="Kayseri için özet bilgiler">
            {topSpecs.map((spec) => (
              <div key={spec.k} className={styles.specItem}>
                <div className={styles.specK}>{spec.k}</div>
                <div className={styles.specV}>{spec.v}</div>
              </div>
            ))}
          </div>

          <div className={styles.ctaRow}>
            <a href={waUrl} target="_blank" rel="noopener noreferrer" className={`btn btn-primary ${styles.ctaBtn}`}>
              <IconWhatsApp />
              <span>WhatsApp ile Teklif Al</span>
            </a>
            <a href={`tel:${SALES_PHONE_READABLE.replace(/\s/g, '')}`} className={`btn btn-outline ${styles.ctaBtn}`}>
              <IconPhone />
              <span>{SALES_PHONE_READABLE}</span>
            </a>
          </div>
        </section>
      </div>

      <div className={styles.contentGrid}>
        <section>
          <div className={`card ${styles.sectionCard}`} style={{ padding: 0 }}>
            <Tabs items={tabItems} defaultKey="aciklama" />
          </div>
        </section>
      </div>

      {pageReviews.length > 0 && (
        <section className={styles.reviewSection}>
          <div className={styles.reviewHeader}>
            <h2 className={styles.reviewTitle}>Müşteri Değerlendirmeleri</h2>
            <div className={styles.reviewUnderline} aria-hidden="true" />
          </div>
          <div className={styles.reviewGrid}>
            {pageReviews.map((review, idx) => (
              <div key={idx} className={styles.reviewCard}>
                <div className={styles.reviewTop}>
                  <div className={styles.reviewStars}>
                    {[1, 2, 3, 4, 5].map((star) => (
                      <IconStar key={star} style={{ opacity: star <= review.rating ? 1 : 0.3 }} />
                    ))}
                  </div>
                  {review.verified && <span className={styles.verifiedBadge}>✓ Doğrulanmış</span>}
                </div>
                <h3 className={styles.reviewName}>{review.title}</h3>
                <p className={styles.reviewText}>{review.text}</p>
                <div className={styles.reviewFooter}>
                  <span className={styles.reviewAuthor}>{review.name}</span>
                  <span className={styles.reviewSeparator}>•</span>
                  <span className={styles.reviewDate}>{review.date}</span>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {kayseriProduct.faqs?.length ? (
        <section className={styles.faqSection}>
          <div className={styles.faqHeader}>
            <h2 className={styles.faqTitle}>Kayseri Bondex Sünger – Sıkça Sorulan Sorular</h2>
            <div className={styles.faqUnderline} aria-hidden="true" />
          </div>
          <div className={styles.faqCard}>
            <Accordion items={kayseriProduct.faqs} variant="seo" />
          </div>
        </section>
      ) : null}

      {relatedBondex.length ? (
        <section className={styles.relatedSection}>
          <div className={styles.relatedHeader}>
            <h2 className={styles.relatedTitle}>Diğer Bondex Sünger Modelleri</h2>
            <div className={styles.relatedUnderline} aria-hidden="true" />
          </div>
          <div className={styles.relatedGrid}>
            {relatedBondex.map((p) => (
              <div key={p.slug} className={styles.relatedItem}>
                <ProductCard product={p} />
              </div>
            ))}
          </div>
        </section>
      ) : null}
    </main>
  );
}
